import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { observer } from 'mobx-react-lite'

import userStore from 'entities/user/module/user-store'

import { UserApi } from 'shared/api/UserApi'
import { refreshOrdersCount } from 'shared/api/orders-count-query'
import ErrorImage from 'shared/images/error.png'
import { getErrorMessage } from 'shared/lib/errorText'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { PopupContent } from 'shared/ui/poup-content'

import { mapOrdersToCategories } from '../../lib/mapOrdersToCategories'
import {
  InsuranceOrderCardProps,
  MyOrderListCardProps,
  OrdersResponse,
  TourDocumentOrderCardProps,
} from '../../model'
import { InsuranceOrderCard } from '../insurance-order-card'
import { MyOrderCard } from '../my-order-card'
import { MyOrderEmpty } from '../my-order-empty'
import { MyOrderListSkeleton } from '../my-order-list-skeleton'
import { TourOrderCard } from '../tour-order-card'

import s from './styles.module.scss'

interface Props {
  isActive?: boolean
  onPayInsuranceOrder?: (order: InsuranceOrderCardProps) => void
}

const isInsuranceOrderCard = (
  card: MyOrderListCardProps,
): card is InsuranceOrderCardProps =>
  'type' in card && card.type === 'insurance'

const isTourDocumentOrderCard = (
  card: MyOrderListCardProps,
): card is TourDocumentOrderCardProps =>
  'type' in card && card.type === 'tour_document'

export const MyOrderList = observer((props: Props) => {
  const { isActive = false, onPayInsuranceOrder } = props
  const navigate = useNavigate()
  const [orders, setOrders] = useState<OrdersResponse>([])
  const [loading, setLoading] = useState(false)
  const user = userStore.account

  const { t } = useTranslation()

  const { openDialog, closeDialog } = useDialogBottomSheet()

  const onShowError = (errorText?: string) => {
    openDialog({
      isDialog: false,
      child: (
        <PopupContent
          image={ErrorImage}
          title={t('error')}
          button={t('close')}
          description={errorText || t('error_des')}
          onClick={closeDialog}
        />
      ),
    })
  }

  const getOrders = async () => {
    try {
      setLoading(true)

      const { data } = await UserApi.getMyOrders(String(isActive))

      setOrders(data?.data ?? [])
      void refreshOrdersCount()
    } catch (error) {
      onShowError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  const categoriesWithCards = useMemo(
    () => mapOrdersToCategories(orders, isActive),
    [orders, isActive],
  )

  useEffect(() => {
    if (user) void getOrders()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  return (
    <div className={s.list}>
      {loading ? (
        <MyOrderListSkeleton />
      ) : categoriesWithCards.length === 0 ? (
        <MyOrderEmpty />
      ) : (
        categoriesWithCards.map(({ categoryLabel, cards }, index) => (
          <div
            key={`${categoryLabel}-${index}`}
            className={s.category}
          >
            <p className="bold-16">{categoryLabel}</p>
            {cards.map((card) =>
              isInsuranceOrderCard(card) ? (
                <InsuranceOrderCard
                  key={card.id}
                  {...card}
                  onPay={
                    onPayInsuranceOrder
                      ? () => onPayInsuranceOrder(card)
                      : undefined
                  }
                />
              ) : isTourDocumentOrderCard(card) ? (
                <TourOrderCard
                  key={card.id}
                  order={card}
                  onOpen={() => navigate(card.detailPath)}
                />
              ) : (
                <MyOrderCard
                  key={card.id}
                  {...card}
                  onClick={() => card.detailPath && navigate(card.detailPath)}
                />
              ),
            )}
          </div>
        ))
      )}
    </div>
  )
})
